import type { JSX, MemoExoticComponent } from 'react'
import type { SkinView } from '../../index.h'
import type { Scope, ScopeList } from '../core'
import type { Recursive } from './Recursive'

import { D } from '@mobily/ts-belt'
import { useGridCollapseLimit } from "/app/libraries/arcana/src/blocks/Grid/hooks/index.ts"

import { memo, useMemo } from 'react'
import { exists } from '~/shared/helpers'
import { key, useRaw } from '../core'
import { ManyItem } from './ManyItem'

export type MapSkinView = SkinView & Required<Pick<SkinView, 'map'>>
export type NoMapSkinView = Omit<SkinView, 'map'>

interface Props {
  scopeList: ScopeList
  skin: MapSkinView
  parent: Scope
  Recursive: Recursive
  [key: string]: unknown // any additional props, will be propogated to all elements in list
}

// exports memoized component
export const Map: MemoExoticComponent<typeof Map_> = memo(Map_)

// Map_.whyDidYouRender = true
Map_.displayName = 'Map'
function Map_({
  scopeList,
  skin,
  parent,
  Recursive,
  ...props
}: Props): JSX.Element | null {
  const { list: rawList, name, index } = scopeList
  const list = useRaw(rawList) as unknown[] | null
  const collapseLimit = useGridCollapseLimit()

  // it is crucial to omit `map` field from skin, to prevent infinite loop
  const skinNoMap: NoMapSkinView = useMemo(
    () => D.deleteKey(skin, 'map'),
    [skin]
  )

  // memoized render each element in list
  const kiddos: JSX.Element[] | null = useMemo(() => {
    if (!list) return null
    const visibleList =
      collapseLimit != null ? list.slice(0, collapseLimit) : list
    return visibleList.map((item, i) => {
      return (
        <ManyItem
          {...props}
          key={key(skin, i)}
          i={i}
          item={item}
          name={name}
          index={index}
          skin={skinNoMap}
          parent={parent}
          Recursive={Recursive}
        />
      )
    })
  }, [
    Recursive,
    collapseLimit,
    index,
    list,
    name,
    parent,
    props,
    skin,
    skinNoMap,
  ])

  return kiddos ? <>{kiddos}</> : null
}

export function hasMap(skin: SkinView): skin is MapSkinView {
  return exists(skin.map)
}
